import { useReservation } from '@/contexts';
import { ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { PortalOutletContext } from './portal/PortalShellContainer';
import Header from './Header';
import MobileView from './MobileView';
import { Button } from './ui';

// Helper function to format dates (shared across pages)
function formatDate(input: string | Date | null | undefined): string {
  const s = String(input ?? '').trim();
  if (!s) return '—';

  // Prefer the literal date part if present (guarantees same day)
  const dateOnly = (s.match(/^(\d{4}-\d{2}-\d{2})/) || [])[1];
  if (dateOnly) {
    const [y, mo, d] = dateOnly.split('-').map(Number);
    const date = new Date(y, mo - 1, d);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  // Else: parse whatever it is; stabilize zone if needed
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(
    dt
  );
}

// Helper to format time from timestamp in property's timezone
function formatTime(timestamp: string | null | undefined, timezone?: string | null): string {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    // Use property timezone if available, otherwise browser default
    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    if (timezone) {
      options.timeZone = timezone;
    }
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch {
    return '';
  }
}

// Reservation summary component (standardized across pages)
interface ReservationSummaryProps {
  listingName: string;
  arrivalDate: string;
  departureDate: string;
  arrivalTime?: string | null;
  departureTime?: string | null;
  timezone?: string | null;
  showTitleBelow?: boolean;
  titleBelow?: string | null;
  subtitleBelow?: string | null;
  hideImage?: boolean;
}

function ReservationSummary({
  listingName,
  arrivalDate,
  departureDate,
  arrivalTime,
  departureTime,
  timezone,
  listingAvatarUrl,
  showTitleBelow,
  titleBelow,
  subtitleBelow,
  hideImage,
}: ReservationSummaryProps & {
  listingAvatarUrl?: string | null;
  arrivalTime?: string | null;
  departureTime?: string | null;
  timezone?: string | null;
}) {
  return (
    <div className="mb-6">
      {/* Title and dates - with tucked in effect */}
      <div className="px-2">
        {/* Title */}
        <h1 className="text-[28px] leading-tight sm:text-[34px] font-semibold text-[#1E3A5F] tracking-tight mb-3 pb-3 border-b border-gray-200">
          {listingName}
        </h1>

        {/* Check-in and Check-out row */}
        <div className="flex items-start gap-0 mb-4">
          {/* Check-in */}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em] mb-1">Check-in:</div>

            {/* one line: date + time (prevent wrap) */}
            <div className="text-[14px] sm:text-base font-semibold text-[#1E3A5F] leading-snug">
              <span className="whitespace-nowrap">{formatDate(arrivalDate)}</span>
              {arrivalTime && (
                <>
                  {' '}
                  <span className="text-gray-500 font-normal whitespace-nowrap text-[13px] sm:text-sm">
                    at {formatTime(arrivalTime, timezone)}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="w-px bg-gray-300 mx-2 sm:mx-3 self-stretch" />

          {/* Check-out */}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em] mb-1">Check-out:</div>

            {/* one line: date + time (prevent wrap) */}
            <div className="text-[14px] sm:text-base font-semibold text-[#1E3A5F] leading-snug">
              <span className="whitespace-nowrap">{formatDate(departureDate)}</span>
              {departureTime && (
                <>
                  {' '}
                  <span className="text-gray-500 font-normal whitespace-nowrap text-[13px] sm:text-sm">
                    at {formatTime(departureTime, timezone)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Photo - full width with optional title overlay */}
      {!hideImage && listingAvatarUrl && (
        <div className="relative -mx-[6px]">
          {/* soft fade at top of card - always show */}
          <div
            className="pointer-events-none absolute inset-x-0 z-10"
            style={{
              top: '-2px',
              height: 80,
              background:
                'linear-gradient(to bottom, rgba(255,255,255,1) 10%, rgba(255,255,255,0.1) 70%, transparent 100%)',
            }}
          />
          <div className="bg-white border-b border-l border-r border-gray-200 rounded-b-2xl shadow-sm overflow-hidden">
            <div className="relative w-full">
              {/* 16:9, larger radius feel, full width */}
              <div className="aspect-[16/9] w-full">
                <img
                  src={listingAvatarUrl}
                  alt={listingName}
                  className="w-full h-full object-cover"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Title below image (for damage waiver) */}
      {showTitleBelow && titleBelow && (
        <div className="mt-6 text-center px-2">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{titleBelow}</h2>
          {subtitleBelow && <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{subtitleBelow}</p>}
        </div>
      )}
    </div>
  );
}

// Status pill component (standardized across pages)
type TaskStatus = 'not_started' | 'in_progress' | 'completed';

interface StatusPillProps {
  status: TaskStatus;
}

function StatusPill({ status }: StatusPillProps) {
  const map = {
    completed: 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm',
    in_progress: 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-sm',
    not_started: 'bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-sm',
  } as const;

  const iconMap = {
    completed: (
      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    ),
    in_progress: (
      <svg className="w-3 h-3 mr-1 animate-spin" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10 3a7 7 0 100 14 7 7 0 000-14zM10 1a9 9 0 110 18 9 9 0 010-18z" opacity="0.25" />
        <path d="M10 1a9 9 0 011 17.945V19a1 1 0 01-2 0v-.055A9 9 0 0110 1z" />
      </svg>
    ),
    not_started: (
      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
  } as const;

  const label = status === 'completed' ? 'Completed' : status === 'in_progress' ? 'In progress' : 'Not started';

  return (
    <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${map[status]}`}>
      {iconMap[status]}
      {label}
    </span>
  );
}

// Task button component (standardized across pages)
interface TaskButtonProps {
  status: TaskStatus;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}

function TaskButton({ status, onClick, disabled, children }: TaskButtonProps) {
  function getButtonIcon(status: TaskStatus) {
    if (status === 'completed') {
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
    if (status === 'in_progress') {
      return (
        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    );
  }

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 ${
        status === 'completed'
          ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
          : status === 'in_progress'
            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md'
      }`}
    >
      <div className="flex items-center gap-2">
        {getButtonIcon(status)}
        <span>{children}</span>
      </div>
    </Button>
  );
}

// Task card component (standardized across pages)
interface TaskCardProps {
  title: string;
  description: string;
  status: TaskStatus;
  onButtonClick: () => void;
  buttonText: string;
  disabled?: boolean;
}

function TaskCard({ title, description, status, onButtonClick, buttonText, disabled }: TaskCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <StatusPill status={status} />
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        <div className="flex justify-end">
          <TaskButton status={status} onClick={onButtonClick} disabled={disabled}>
            {buttonText}
          </TaskButton>
        </div>
      </div>
    </div>
  );
}

// Task list wrapper component (standardized spacing)
interface TaskListProps {
  children: ReactNode;
}

function TaskList({ children }: TaskListProps) {
  return <div className="space-y-4">{children}</div>;
}

interface BottomCtaProps {
  children: ReactNode;
  sticky?: boolean;
}
function BottomCta({ children, sticky = true }: BottomCtaProps) {
  return (
    <div className={sticky ? 'sticky bottom-0 bg-white pt-6 pb-4 -mx-4 px-4 mt-8' : 'mt-8'}>
      <div className="max-w-2xl mx-auto">{children}</div>
    </div>
  );
}

interface LayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  showReservation?: boolean;
  bottomCta?: ReactNode;
  bottomCtaSticky?: boolean;
  hideTitle?: boolean; // If true, don't show title/subtitle at all
  showTitleBelow?: boolean; // If true, show title/subtitle below the image
}

function Layout({
  children,
  title,
  subtitle,
  showReservation = true,
  bottomCta,
  bottomCtaSticky = true,
  hideTitle = false,
  showTitleBelow = false,
}: LayoutProps) {
  const { summary: reservationSummary } = useReservation();
  // Inside the portal, hide the listing photo in ReservationSummary — the photo
  // already lives in the overview hero / sidebar thumb, so duplicating it on
  // every section page feels redundant. Standalone routes (no Outlet parent)
  // return null here and keep the photo.
  const portalOutlet = useOutletContext<PortalOutletContext | null>();
  const inPortal = portalOutlet != null;

  return (
    <MobileView>
      <div className="px-4 pt-5 pb-6">
        {/* Show header unless hideTitle or showTitleBelow */}
        {!hideTitle && !showTitleBelow && <Header title={title} subtitle={subtitle} />}
        {showReservation && reservationSummary && (
          <ReservationSummary
            {...reservationSummary}
            listingAvatarUrl={reservationSummary.listingAvatarUrl}
            arrivalTime={reservationSummary.arrivalTime}
            departureTime={reservationSummary.departureTime}
            timezone={reservationSummary.timezone}
            showTitleBelow={showTitleBelow}
            titleBelow={showTitleBelow ? title : null}
            subtitleBelow={showTitleBelow ? subtitle : null}
            hideImage={inPortal}
          />
        )}
        <main className="main-content max-w-[720px] mx-auto">{children}</main>
        {bottomCta && <BottomCta sticky={bottomCtaSticky}>{bottomCta}</BottomCta>}
      </div>
    </MobileView>
  );
}

// Export components for use in pages
export { StatusPill, TaskButton, TaskCard, TaskList };
export type { TaskStatus };
export default Layout;
